import { message } from 'antd';
import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';
import { useMutateCourses } from '@/store';

const CourseService = sdk.getService('CourseService');

type State = {
  isSimpleModalOpen: boolean;
  isBeingUsedModalOpen: boolean;
  isCheckingUsage: boolean;
};

type Action =
  | { type: 'START_CHECKING' }
  | { type: 'OPEN_SIMPLE_MODAL' }
  | { type: 'OPEN_BEING_USED_MODAL' }
  | { type: 'CLOSE_SIMPLE_MODAL' }
  | { type: 'CLOSE_BEING_USED_MODAL' }
  | { type: 'CLOSE_ALL_MODALS' }
  | { type: 'FINISH_CHECKING' };

const initialState: State = {
  isSimpleModalOpen: false,
  isBeingUsedModalOpen: false,
  isCheckingUsage: false
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START_CHECKING':
      return { ...state, isCheckingUsage: true };
    case 'FINISH_CHECKING':
      return { ...state, isCheckingUsage: false };
    case 'OPEN_SIMPLE_MODAL':
      return { ...state, isSimpleModalOpen: true, isCheckingUsage: false };
    case 'OPEN_BEING_USED_MODAL':
      return { ...state, isBeingUsedModalOpen: true, isCheckingUsage: false };
    case 'CLOSE_SIMPLE_MODAL':
      return { ...state, isSimpleModalOpen: false };
    case 'CLOSE_BEING_USED_MODAL':
      return { ...state, isBeingUsedModalOpen: false };
    case 'CLOSE_ALL_MODALS':
      return { ...state, isSimpleModalOpen: false, isBeingUsedModalOpen: false };
    default:
      return state;
  }
};

export const useBaseContent = (courseId: string) => {
  const { t } = useTranslation('ManagerBaseContentTranslations');
  const { removeCourse } = useMutateCourses();
  const [state, dispatch] = useReducer(reducer, initialState);

  const checkAndOpenDeleteModal = async () => {
    dispatch({ type: 'START_CHECKING' });

    try {
      const { isBeingUsed } = await CourseService.checkIfBaseContentIsBeingUsed(courseId);

      if (isBeingUsed) {
        dispatch({ type: 'OPEN_BEING_USED_MODAL' });
      } else {
        dispatch({ type: 'OPEN_SIMPLE_MODAL' });
      }
    } catch (error) {
      console.error('Error checking if base content is being used:', error);
      message.error(t('ERROR_CHECKING_USAGE'));
      dispatch({ type: 'FINISH_CHECKING' });
    }
  };

  const handleDelete = () => {
    removeCourse(courseId);
    dispatch({ type: 'CLOSE_ALL_MODALS' });
  };

  const closeSimpleModal = () => {
    dispatch({ type: 'CLOSE_SIMPLE_MODAL' });
  };

  const closeBeingUsedModal = () => {
    dispatch({ type: 'CLOSE_BEING_USED_MODAL' });
  };

  return {
    ...state,
    checkAndOpenDeleteModal,
    handleDelete,
    closeSimpleModal,
    closeBeingUsedModal
  };
};
